// The canvas grid — react-grid-layout driven entirely by the store. It reads
// the active section's placements, renders each widget in a WidgetFrame, and
// pushes every drag/resize back into the store (which the Save button then
// persists). One fixed 12-column desktop grid (mobile deferred, §7B).

import { useCallback, useRef, useState } from "react";
import GridLayout, { WidthProvider, type Layout } from "react-grid-layout";
import { GRID_COLUMNS } from "@catbento/shared";
import type { LayoutSection } from "@catbento/shared";
import { useCanvasStore } from "../store/canvas.js";
import { gridColWidth } from "../store/placements.js";
import { getWidget } from "../widgets/registry.js";
import type { WidgetScope } from "../widgets/scope.js";
import WidgetFrame from "./WidgetFrame.js";
import { theme } from "../lib/theme.js";

// WidthProvider measures the container and feeds RGL its pixel width, so the
// 12-col grid fills whatever space the canvas column has.
const Grid = WidthProvider(GridLayout);

// Grid geometry — shared between RGL and the background-grid overlay so the
// faint guide lines line up exactly with where widgets snap (#6).
const ROW_HEIGHT = 40;
const MARGIN = 12;
const ROW_PITCH = ROW_HEIGHT + MARGIN;

/** Small fallback tile for a saved widgetKey that's no longer in the registry
 *  (e.g. a widget was renamed/removed) — degrade, don't crash. */
function UnknownWidget({ widgetKey }: { widgetKey: string }) {
  return (
    <div style={{ color: theme.inkFaint, fontSize: 13 }}>
      Unknown widget: <code>{widgetKey}</code>
    </div>
  );
}

/** Faint grid lines behind the widgets so the user can see the snap steps (#6).
 *  Uses the SAME column-width formula as RGL, from the measured container width. */
function GridBackground({ width }: { width: number }) {
  const colWidth = gridColWidth(width, GRID_COLUMNS, MARGIN, MARGIN);
  if (colWidth <= 0) return null;
  const colPitch = colWidth + MARGIN;
  const line = "rgba(148,163,184,0.10)"; // theme.inkMuted, very faint
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        borderRadius: 12,
        backgroundImage: `repeating-linear-gradient(to right, ${line} 0, ${line} 1px, transparent 1px, transparent ${colPitch}px), repeating-linear-gradient(to bottom, ${line} 0, ${line} 1px, transparent 1px, transparent ${ROW_PITCH}px)`,
        backgroundPosition: `${MARGIN}px ${MARGIN}px`,
      }}
    />
  );
}

export default function Canvas({ section, scope }: { section: LayoutSection; scope: WidgetScope }) {
  const placements = useCanvasStore((s) => s.placements[section]);
  const removeWidget = useCanvasStore((s) => s.removeWidget);
  const applyLayout = useCanvasStore((s) => s.applyLayout);

  // Measure the canvas width so the background grid matches RGL's columns.
  // A CALLBACK ref (not a mount-time effect): the measured wrapper only exists in
  // the non-empty branch below, so it mounts AFTER Canvas first renders (which
  // may be the empty state). The callback attaches the observer exactly when the
  // node appears and disconnects when it leaves — an effect with [] deps would
  // run once while the ref was still null and never re-attach (the bug that left
  // the grid at width 0 / invisible).
  const [width, setWidth] = useState(0);
  const roRef = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === "number") setWidth(w);
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);

  if (placements.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 320,
          border: `1px dashed ${theme.border}`,
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          color: theme.inkFaint,
          fontSize: 14,
        }}
      >
        Empty canvas — add a widget from the left, then Save.
      </div>
    );
  }

  const layout: Layout[] = placements.map((p) => ({
    i: p.instanceId,
    x: p.position.x,
    y: p.position.y,
    w: p.position.w,
    h: p.position.h,
  }));

  return (
    <div ref={measureRef} style={{ flex: 1, position: "relative", minHeight: 320 }}>
      <GridBackground width={width} />
      <Grid
        className="layout"
        style={{ position: "relative", zIndex: 1 }}
        layout={layout}
        cols={GRID_COLUMNS}
        rowHeight={ROW_HEIGHT}
        margin={[MARGIN, MARGIN]}
        draggableHandle=".canvas-drag"
        draggableCancel=".canvas-no-drag"
        onLayoutChange={(l: Layout[]) => applyLayout(section, l)}
        isBounded
      >
        {placements.map((p) => {
          const def = getWidget(p.widgetKey);
          const Body = def?.component;
          return (
            <div key={p.instanceId}>
              <WidgetFrame
                title={def?.title ?? p.widgetKey}
                onRemove={() => removeWidget(section, p.instanceId)}
              >
                {Body ? (
                  <Body scope={scope} instanceId={p.instanceId} />
                ) : (
                  <UnknownWidget widgetKey={p.widgetKey} />
                )}
              </WidgetFrame>
            </div>
          );
        })}
      </Grid>
    </div>
  );
}
